import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import {
  UserKeys,
  getUserList,
  getGroups,
  getBetaListForwardingAddressesQuery,
} from '../../../../../api/users-api';
import {
  GroupHeader,
  GroupItems,
} from '../../../../../shared/autocomplete-groups-ui';
import useDebounce from '../../../../../utils/useDebounce';
import UserAvatar from '../../../../../shared/user-avatar';

import Autocomplete from '@mui/material/Autocomplete';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import CircularProgress from '@mui/material/CircularProgress';
import ListItem from '@mui/material/ListItem';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import { styled } from '@mui/material/styles';
import ArchiveRounded from '@mui/icons-material/ArchiveRounded';
import Check from '@mui/icons-material/Check';
import DoNotDisturb from '@mui/icons-material/DoNotDisturb';
import Group from '@mui/icons-material/Group';

interface ForwardingAutocompleteProps {
  userEmail: string;
  autocompleteValue: string;
  setAutoCompleteValue: (value: string) => void;
  setAutoCompleteTypeValue?: (value: string) => void;
  setClearWasClicked?: (value: boolean) => void;
  size?: 'small' | 'medium';
  disabled?: boolean;
  newKey?: string;
  errors?: boolean;
  helperText?: string;
}

export default function ForwardingAutocomplete({
  size,
  disabled,
  autocompleteValue,
  setAutoCompleteValue,
  setClearWasClicked,
  newKey,
  setAutoCompleteTypeValue,
  errors,
  helperText,
  userEmail,
}: ForwardingAutocompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const debouncedInput = useDebounce(inputValue, 500);

  const { data: userData, isFetching: userLoading } = useQuery({
    queryKey: [UserKeys.UserList, { debouncedInput }],
    queryFn: () =>
      getUserList(
        {
          cursor: null,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 5,
        },
        debouncedInput,
        '',
        null,
        null
      ),
    enabled: debouncedInput !== '' && autocompleteValue === '',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const { data: groupData, isFetching: groupsLoading } = useQuery({
    queryKey: [UserKeys.Groups, { debouncedInput }],
    queryFn: () =>
      getGroups(
        {
          cursor: null,
          results: [],
          nameSpace: '',
          firstPull: true,
          searchTime: 0,
          pageSize: 5,
        },
        debouncedInput
      ),
    enabled: debouncedInput !== '' && autocompleteValue === '',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: keepPreviousData,
    staleTime: 1000 * 60 * 1,
  });

  const { data: forwardingEmails } = useQuery(
    getBetaListForwardingAddressesQuery(userEmail)
  );

  interface Option {
    email: string;
    type: string;
    firstName?: string;
    lastName?: string;
    suspended?: boolean;
    archived?: boolean;
    numberOfMembers?: number;
    verificationStatus?: string;
  }

  const userOptions: Option[] = (
    Array.isArray(userData?.results) ? userData.results : []
  ).map((result) => ({
    email: result.userEmail,
    type: 'user',
    firstName: result.firstName,
    lastName: result.lastName,
    suspended: result.suspended,
    archived: result.archived,
  }));

  const groupOptions: Option[] = (
    Array.isArray(groupData?.results) ? groupData.results : []
  ).map((result) => ({
    email: result.email,
    type: 'group',
    numberOfMembers: result.directMemberCount,
  }));

  const forwardEmailOptions: Option[] = Array.isArray(forwardingEmails)
    ? forwardingEmails.map((forwardingEmail) => ({
        email: forwardingEmail.forwardingEmail,
        type: 'forwarding',
        verificationStatus: forwardingEmail.verificationStatus,
      }))
    : [];

  const options = [...userOptions, ...groupOptions, ...forwardEmailOptions];

  function createNoOptionsText() {
    let text = 'No results found.';
    if (debouncedInput === '') {
      text = 'Start typing to search...';
    } else if (userOptions.length === 0 && groupData?.results.length === 0) {
      text = 'No results found.';
    }

    return text;
  }

  const key = useMemo(
    () => newKey || new Date().getTime().toString(),
    [newKey]
  );

  const SmallAvatar = styled(Avatar)(() => ({
    width: 16,
    height: 16,
    backgroundColor: 'white',
  }));

  return (
    <Autocomplete
      id="shared-autocomplete"
      key={key}
      disabled={disabled}
      filterOptions={(x) => x}
      options={options}
      size={size}
      componentsProps={{
        paper: {
          sx: {
            minWidth: 350,
          },
        },
      }}
      autoComplete
      autoHighlight
      includeInputInList
      filterSelectedOptions
      noOptionsText={createNoOptionsText()}
      getOptionLabel={(option) => {
        if (typeof option === 'string') {
          return (
            userData?.results.find((user) => user.userEmail === option)
              ?.userEmail || option
          );
        } else {
          return option.email;
        }
      }}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'string') {
          return option === value;
        } else {
          return option.email === value?.email;
        }
      }}
      // value={autocompleteValue}
      onChange={(_event, newValue, reason) => {
        if (reason === 'clear') {
          setClearWasClicked?.(true);
        }

        if (typeof newValue === 'string') {
          setAutoCompleteValue(newValue);
        } else {
          if (setAutoCompleteTypeValue) {
            setAutoCompleteTypeValue(newValue?.type || '');
          }

          if (newValue?.type === 'calendar') {
            setAutoCompleteValue(newValue?.firstName || '');
          } else {
            setAutoCompleteValue(newValue?.email || '');
          }
        }
      }}
      getOptionDisabled={(option) => {
        return (
          option.type === 'forwarding' &&
          option.verificationStatus !== 'accepted'
        );
      }}
      renderOption={(props, option: Option) => {
        return (
          <ListItem {...props} dense>
            {option.type === 'user' ? (
              <>
                <ListItemAvatar>
                  <Badge
                    overlap="circular"
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    badgeContent={
                      <SmallAvatar
                        alt={`${option.firstName} ${option.lastName}`}
                        sx={{
                          border: (theme) =>
                            `1px solid ${
                              option.suspended
                                ? theme.palette.warning.main
                                : option.archived
                                ? theme.palette.info.main
                                : theme.palette.success.main
                            }`,
                        }}
                      >
                        {option.suspended ? (
                          <DoNotDisturb color="warning" sx={{ width: 10 }} />
                        ) : option.archived ? (
                          <ArchiveRounded color="info" sx={{ width: 10 }} />
                        ) : (
                          <Check color="success" sx={{ width: 10 }} />
                        )}
                      </SmallAvatar>
                    }
                    sx={{ mr: 2 }}
                  >
                    <UserAvatar userEmail={option.email} />
                  </Badge>
                </ListItemAvatar>
                <ListItemText
                  id={option.email}
                  primary={option.email}
                  secondary={`${option.firstName} ${option.lastName}`}
                />
              </>
            ) : option.type === 'group' ? (
              <>
                <ListItemAvatar>
                  <Avatar>
                    <Group />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  id={option.email}
                  primary={option.email}
                  secondary={`${option.numberOfMembers} members`}
                  primaryTypographyProps={{ whiteSpace: 'normal' }}
                />
              </>
            ) : (
              <>
                <ListItemAvatar>
                  <UserAvatar userEmail={option.email} />
                </ListItemAvatar>
                <ListItemText
                  id={option.email}
                  primary={option.email}
                  secondary={option.verificationStatus}
                  primaryTypographyProps={{ whiteSpace: 'normal' }}
                />
              </>
            )}
          </ListItem>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={'Search for a user or group'}
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {userLoading || groupsLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          error={errors}
          helperText={helperText}
        />
      )}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      groupBy={(option) => option.type}
      renderGroup={(params) => (
        <li key={params.key}>
          <GroupHeader>
            {params.group === 'user'
              ? 'Users'
              : params.group === 'group'
              ? 'Groups'
              : 'Forwarding Addresses'}
          </GroupHeader>
          <GroupItems>{params.children}</GroupItems>
        </li>
      )}
    />
  );
}
